import type { Survey, Question } from '../components/survey/survey.types';
import { generateGUID } from './guidGenerator';
import type { UTMParameters } from './trackingUtils';

export interface SubmissionAnswer {
  question_id: string;
  answers: string[];
}

export interface SurveySubmission {
  survey_id: string;
  submission_id: string;
  submission_date: string;
  marketo_cookie?: string | null;
  language: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_content?: string;
  answers: SubmissionAnswer[];
}

/**
 * Transforms a star rating response to submission format
 */
function transformStarRating(value: number | null): string[] {
  if (value === null) return [];
  return [value.toString()];
}

/**
 * Transforms a multiple choice response to submission format
 * Looks up English text for fixed choices, preserves user text for "other" responses
 */
function transformMultipleChoice(
  value: string[],
  question: Question
): string[] {
  if (!value || value.length === 0) return [];

  if (question.question_type !== 'multiple_choice') return [];

  const results: string[] = [];

  for (const item of value) {
    if (item.startsWith('other:')) {
      // Extract user's custom text and format as "Other-<user text>"
      const customText = item.substring(6); // Remove "other:" prefix
      results.push(`Other-${customText}`);
    } else if (item.startsWith('choice_')) {
      // Lookup English text for the choice
      const choiceNumber = parseInt(item.split('_')[1], 10);
      const choice = question.choices?.find((c) => c.choice_number === choiceNumber);
      if (choice) {
        results.push(choice.choice_text.en);
      }
    }
  }

  return results;
}

/**
 * Transforms a dropdown response to submission format
 * Looks up English text for fixed choices, preserves user text for "other" responses
 */
function transformDropdown(
  value: string,
  question: Question
): string[] {
  if (!value || value.trim() === '') return [];

  if (question.question_type !== 'dropdown') return [];

  if (value.startsWith('other:')) {
    // Extract user's custom text and format as "Other-<user text>"
    const customText = value.substring(6); // Remove "other:" prefix
    return [`Other-${customText}`];
  } else if (value.startsWith('choice_')) {
    // Lookup English text for the choice
    const choiceNumber = parseInt(value.split('_')[1], 10);
    const choice = question.choices?.find((c) => c.choice_number === choiceNumber);
    if (choice) {
      return [choice.choice_text.en];
    }
  }

  return [];
}

/**
 * Transforms a matrix response to submission format
 * Splits into multiple sub-questions (e.g., 3.1, 3.2, 3.3)
 * Returns English text for each answer
 */
function transformMatrix(
  value: Record<number, number>,
  question: Question
): SubmissionAnswer[] {
  if (!value || Object.keys(value).length === 0) return [];

  if (question.question_type !== 'matrix') return [];

  const results: SubmissionAnswer[] = [];

  // Iterate through each row
  for (const [rowKey, answerNumber] of Object.entries(value)) {
    const rowQuestionNumber = parseInt(rowKey, 10);

    // Find the position of this sub-question within the matrix questions array
    let subIndex = -1;
    if (question.question_type === 'matrix' && question.questions) {
      subIndex = question.questions.findIndex(
        (sq) => sq.question_number === rowQuestionNumber
      );
    }

    // Use positional index (1-based) when available (e.g. 5.1, 5.2, 5.3)
    // Fall back to the rowQuestionNumber if we can't resolve the index
    const subQuestionSuffix = subIndex >= 0 ? subIndex + 1 : rowQuestionNumber;
    const subQuestionNumber = `${question.question_number}.${subQuestionSuffix}`;

    // Find the English text for this answer
    const answer = question.answers?.find((a) => a.answer_number === answerNumber);
    const answerText = answer ? answer.answer_text.en : '';

    results.push({
      question_id: subQuestionNumber,
      answers: [answerText],
    });
  }

  return results;
}

/**
 * Transforms a number rating response to submission format
 */
function transformNumberRating(value: number | null): string[] {
  if (value === null) return [];
  return [value.toString()];
}

/**
 * Transforms text field responses (single or multi-line) to submission format
 * Preserves the text in whatever language the user typed it
 */
function transformTextQuestion(value: string): string[] {
  if (!value || value.trim() === '') return [];
  return [value];
}

/**
 * Generates the complete survey submission JSON object
 */
export function generateSubmissionJSON(
  surveyData: Survey,
  responses: Record<number, string | number | string[] | Record<number, number>>,
  currentLanguage: string,
  surveyId: string,
  marketoCookie: string | null = null,
  utmParameters: UTMParameters = {}
): SurveySubmission {
  const answers: SubmissionAnswer[] = [];

  // Get all questions from all pages
  const allQuestions: Question[] = [];
  for (const page of surveyData.pages) {
    allQuestions.push(...page.questions);
  }

  // Transform each response based on question type
  for (const question of allQuestions) {
    const questionNumber = question.question_number;
    const response = responses[questionNumber];

    // Skip if no response provided
    if (response === undefined || response === null) {
      continue;
    }

    switch (question.question_type) {
      case 'star_rating': {
        const transformed = transformStarRating(response as number);
        if (transformed.length > 0) {
          answers.push({
            question_id: questionNumber.toString(),
            answers: transformed,
          });
        }
        break;
      }

      case 'multiple_choice': {
        const transformed = transformMultipleChoice(response as string[], question);
        if (transformed.length > 0) {
          answers.push({
            question_id: questionNumber.toString(),
            answers: transformed,
          });
        }
        break;
      }

      case 'matrix': {
        // Matrix questions expand into multiple sub-questions
        const matrixAnswers = transformMatrix(response as Record<number, number>, question);
        answers.push(...matrixAnswers);
        break;
      }

      case 'number_rating': {
        const transformed = transformNumberRating(response as number);
        if (transformed.length > 0) {
          answers.push({
            question_id: questionNumber.toString(),
            answers: transformed,
          });
        }
        break;
      }

      case 'multi_line_text':
      case 'single_line_text':
      case 'email': {
        const transformed = transformTextQuestion(response as string);
        if (transformed.length > 0) {
          answers.push({
            question_id: questionNumber.toString(),
            answers: transformed,
          });
        }
        break;
      }

      case 'dropdown': {
        const transformed = transformDropdown(response as string, question);
        if (transformed.length > 0) {
          answers.push({
            question_id: questionNumber.toString(),
            answers: transformed,
          });
        }
        break;
      }

      default: {
        const _exhaustiveCheck: never = question;
        console.warn(`Unknown question type:`, _exhaustiveCheck);
        break;
      }
    }
  }

  // Generate submission object
  const submission: SurveySubmission = {
    survey_id: surveyId,
    submission_id: generateGUID(),
    submission_date: new Date().toISOString(),
    language: currentLanguage.toUpperCase(),
    answers,
  };

  // Add marketo cookie if present
  if (marketoCookie) {
    submission.marketo_cookie = marketoCookie;
  }

  // Add UTM parameters if present
  if (utmParameters.utm_campaign) {
    submission.utm_campaign = utmParameters.utm_campaign;
  }
  if (utmParameters.utm_term) {
    submission.utm_term = utmParameters.utm_term;
  }
  if (utmParameters.utm_source) {
    submission.utm_source = utmParameters.utm_source;
  }
  if (utmParameters.utm_medium) {
    submission.utm_medium = utmParameters.utm_medium;
  }
  if (utmParameters.utm_content) {
    submission.utm_content = utmParameters.utm_content;
  }

  return submission;
}
